import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

const VISIBLE = 3;
const CENTER_OFFSET = 1;
const GAP = 30; // px between slides

const CarouselViewport = styled.div`
  position: relative;
  width: 100%;
  max-width:850px;
  height: 400px;
  padding-top:50px;
`;

const Track = styled.div`
  display: flex;
  height: 100%;
  will-change: transform;
  transform: ${({ index }) =>
    `translate3d(-${index * (100 / VISIBLE)}%, 0, 0)`};
  transition: ${({ animate }) => (animate ? "transform 0.6s ease" : "none")};
`;

const Slide = styled.div`
  flex: 0 0 calc((100% - ${GAP * (VISIBLE)}px) / ${VISIBLE});
  margin-right: ${GAP}px;
  background: #f4f4f4;
  border-radius: 12px;
  padding: 20px;
  box-sizing: border-box;
  height: ${({ featured }) => (featured ? "110%" : "100%")};
  margin-top: ${({ featured }) => (featured ? "-5%" : "5%")};
  box-shadow: ${({ featured }) =>
    featured ? "0 6px 18px rgba(0,0,0,0.15)" : "0 3px 10px rgba(0,0,0,0.08)"};
  transition: all 0.3s ease;
  background:#ffffff;


  &:last-child {
    margin-right: 0;
  }
`;

const Title = styled.h3`
  margin: 0 0 10px;
  font-size: 18px;
  color: #222;
`;

const Paragraph = styled.p`
  margin: 0;
  font-size: 14px;
  color: #555;
  line-height: 1.4;
`;

const Titles = styled.div`
    display:flex;
    flex-direction:row;
    flex-wrap:wrap;
    gap:5px;
    padding-top:10px;
`

const EachTitle = styled.div`
    display:flex;
    flex-direction:row;
    padding:5px 10px;
    background:#e5e5e5;
    border-radius:100px;
    font-size:13px;
`

export default function Carousel({
  slides = [],
  interval = 3000
}) {
  const len = slides.length;

  const tripled = useMemo(() => {
    const withIdx = slides.map((s, i) => ({ ...s, _orig: i }));
    return [...withIdx, ...withIdx, ...withIdx];
  }, [slides]);

  const START = len;
  const [index, setIndex] = useState(START);
  const [animate, setAnimate] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    startTimer();
    return stopTimer;
  }, [interval]);

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => setIndex(i => i + 1), interval);
  };
  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const handleTransitionEnd = () => {
    if (index >= START + len) {
      stopTimer();
      setAnimate(false);
      setIndex(i => i - len);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true);
          startTimer();
        });
      });
    } else if (index < START) {
      stopTimer();
      setAnimate(false);
      setIndex(i => i + len);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true);
          startTimer();
        });
      });
    }
  };

  const virtual = ((index - START) % len + len) % len;
  const featuredOrig = (virtual + CENTER_OFFSET) % len;

  return (
    <CarouselViewport>
      <Track index={index} animate={animate} onTransitionEnd={handleTransitionEnd}>
        {tripled.map((s, i) => {
          const featured = s._orig === featuredOrig;
          return (
            <Slide key={`${s._orig}-${i}`} featured={featured}>
              <Title>{s.title}</Title>
              <Paragraph>{s.text}</Paragraph>
              <Titles>
                    {
                        s.roles.map((name) => (
                            <EachTitle>{name}</EachTitle>
                        ))
                    }
              </Titles>
            </Slide>
          );
        })}
      </Track>
    </CarouselViewport>
  );
}

